/**
 * Planning, building and editing — asked of the server.
 *
 * The pipeline that decides what an app becomes (the prompt, the gate, the repair loop,
 * the design kits, the runtime inlined into every published page) used to run in the
 * browser, which meant every visitor downloaded it and changing a sentence in a prompt was
 * a frontend deploy. It runs server-side now; this asks it to.
 *
 * These go to the build service's own origin, a Lambda Function URL, because a build takes
 * the better part of a minute and API Gateway stops at twenty-nine seconds.
 */
import type { AppPlan } from '@/webapp/plan/planTypes';
import type { AttachedText } from '@/webapp/prompt';
import type { StyleBrief } from '@/llm/planTypes';
import { ApiError, request } from './client';

/**
 * How long the build Lambda may run: `timeout` on `WebBuildHandler` in infra/lib/stack.ts.
 *
 * Copied, because the browser cannot import the CDK stack, and pinned by a test that reads
 * the stack and fails when the two disagree. This client aborted at 180s while the server ran
 * to 600s, so every build between the two was finished, charged, and reported here as a
 * failure — the phone had the same bug at 330s.
 */
export const BUILD_LAMBDA_TIMEOUT_MS = 600_000;

/**
 * Past the server's own limit, so giving up means there is genuinely no answer coming.
 *
 * Must stay above `timeout` on WebBuildHandler in infra/lib/stack.ts (the phone mirrors it as
 * BUILD_LAMBDA_TIMEOUT_MS in src/webapp/buildClient.ts). Exported because it is also the age
 * past which a build this browser sent cannot still be running somewhere (buildRun.recover).
 */
export const BUILD_TIMEOUT_MS = BUILD_LAMBDA_TIMEOUT_MS + 30_000;
/**
 * /plan, /edit and /assemble run on the SAME Lambda as /build, with the same 600s limit and a
 * model deadline of about the remaining time less 45s, and the model client retries inside
 * that deadline. So a plan or an edit can also run well past two minutes — and an edit is
 * charged before it runs, so aborting at 120s reported a paid, finished edit as a failure.
 */
const EDIT_TIMEOUT_MS = BUILD_LAMBDA_TIMEOUT_MS + 30_000;

/**
 * How often to ask again while the server says this intent is still running. Its rate limit
 * is 30 requests in five minutes per account; this spends at most fifteen of them in any five.
 */
const IN_PROGRESS_POLL_MS = 20_000;

export interface PlanResult {
  /** Always complete: the scaffold, if the model pass failed. */
  plan: AppPlan;
  refined: boolean;
}

export async function planApp(goal: string): Promise<PlanResult> {
  return request<PlanResult>('/plan', {
    base: 'build',
    method: 'POST',
    body: { goal },
    timeoutMs: EDIT_TIMEOUT_MS,
  });
}

export interface BuiltApp {
  appId: string;
  appName: string;
  /** The finished document, runtime already inlined. */
  html: string;
  appCode: string;
  attempts?: number;
  repairRounds?: number;
  gateFailures?: number;
}

export async function buildApp(input: {
  goal: string;
  appName?: string;
  vibe?: string | null;
  plan?: AppPlan | null;
  /*
   * The MANIFEST of an attached document, never the document.
   *
   * A book does not fit in a prompt, and a model that has seen the text will reproduce it
   * from memory rather than fetch it — so the generator is told what the text is called and
   * what its parts are, and writes an app that reads them with `S.asset(name, id)`. The text
   * itself goes to POST /apps/{appId}/text once the app exists.
   */
  textAsset?: AttachedText | null;
  /*
   * NAMES, not URLs. Nothing has been uploaded at this point — the app does not exist yet to
   * hang an upload on — so the app is written against `S.media(name)` and the addresses are
   * filled in when the page is served.
   */
  media?: string[] | null;
  /** The feel of a reference picture. Colours are excluded by design; the kit owns those. */
  style?: StyleBrief | null;
  /**
   * One value per "build this app", reused by every retry of it.
   *
   * The server claims it before it charges, so a retry of the SAME intent is recognised and
   * costs nothing. Minting a fresh one per attempt would defeat that entirely — see
   * `state/buildIntent.ts`, which keeps one across `retry()` and a reload.
   */
  requestKey?: string;
}, options: { inProgressPollMs?: number } = {}): Promise<BuiltApp> {
  const { requestKey, ...payload } = input;
  const ask = () =>
    request<BuiltApp>('/build', {
      base: 'build',
      method: 'POST',
      body: payload,
      timeoutMs: BUILD_TIMEOUT_MS,
      // A header, because the server reads it before it parses anything else.
      ...(requestKey ? { headers: { 'Idempotency-Key': requestKey } } : {}),
    });
  if (!requestKey) return ask();

  /*
   * A 409 `build_in_progress` means an earlier attempt of this SAME intent is still running —
   * the tab was closed or reloaded mid-build, or the connection dropped, and Try again arrived
   * while the server worked on. The server's word for that is "wait, not pay", so wait: ask
   * again under the same key until the build lands (a free replay) or its claim is released
   * (a failure, refunded, and this attempt becomes the build). Surfacing the raw
   * 'build_in_progress' instead invited the person to give up on a build they had paid for.
   *
   * Bounded by the ceiling, counted from the FIRST 409: anything running then ends within the
   * Lambda's limit, so a key still claimed after that is stuck, not busy.
   */
  let giveUpAt: number | null = null;
  for (;;) {
    try {
      return await ask();
    } catch (e) {
      if (!isBuildInProgress(e)) throw e;
      giveUpAt ??= Date.now() + BUILD_TIMEOUT_MS;
      if (Date.now() >= giveUpAt) throw e;
    }
    await new Promise((r) => setTimeout(r, options.inProgressPollMs ?? IN_PROGRESS_POLL_MS));
  }
}

/** The server is still working on this key: the body's `error` is the code, on a 409. */
export function isBuildInProgress(e: unknown): boolean {
  return e instanceof ApiError && e.status === 409 && e.reason === 'build_in_progress';
}

export async function assembleApp(input: {
  appId: string;
  source: string;
  appName?: string;
  accent?: string;
  description?: string;
}): Promise<string> {
  const { html } = await request<{ html: string }>('/assemble', {
    base: 'build',
    method: 'POST',
    body: input,
    timeoutMs: EDIT_TIMEOUT_MS,
  });
  return html;
}

/**
 * An edit is /edit followed by /assemble — and the appId MUST be the one the app already
 * has.
 *
 * That id is the localStorage namespace baked into the published document. Assembling
 * under a fresh one gives the page a different namespace, and every visitor's saved data
 * silently stops existing. So the id is a required argument rather than something this
 * function could helpfully mint, and it refuses to run without one.
 */
export async function editApp(input: {
  appId: string;
  source: string;
  instruction: string;
  appName?: string;
  goal?: string;
  accent?: string;
  description?: string;
}): Promise<{ appCode: string; html: string; drift?: { reasons: string[] } }> {
  if (!input.appId) {
    throw new ApiError(
      'misconfigured',
      'This app has no id to publish under — editing it would strand the data of anyone already using it.',
    );
  }

  const { appCode, drift } = await request<{
    appCode: string;
    attempts?: number;
    gateFailures?: number;
    /*
     * Set when the edit looks like it REPLACED the app — "add a phone number field" coming back
     * as a different product. The server reports rather than refuses, because only the person
     * who typed it knows what they meant; this client dropped it, so the swap went out
     * unremarked. Passed on for chat to say.
     */
    drift?: { reasons: string[] };
  }>('/edit', {
    base: 'build',
    method: 'POST',
    body: { source: input.source, instruction: input.instruction, ...(input.goal ? { goal: input.goal } : {}) },
    timeoutMs: EDIT_TIMEOUT_MS,
  });

  const html = await assembleApp({
    appId: input.appId,
    source: appCode,
    appName: input.appName,
    accent: input.accent,
    description: input.description,
  });

  return { appCode, html, ...(drift?.reasons?.length ? { drift } : {}) };
}
